import React, { Fragment, useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import {
  getEditedJob,
  getExistedJob,
  getHangerState,
  getJobInputs,
  getJobSettingsState,
  getJoistState,
  getResultState,
  getSettingsState,
  selectAll,
} from '../../../../store/selectors'
import { getUIState } from '../../../../../../utils/selectors'

import {
  getFormErrors,
  jobSettingsActions,
  jobsActions,
  resultActions,
} from '../../../../store/slices'

import { CSVLink } from 'react-csv'
import { AddToJobList, OrderJobList, ResultTable, TableNotes } from './components'
import { IndeterminateSelectBox, DuplicateJobError } from '../../../../components'
import { Button, LoadingIndicator, NoticeMessage } from '../../../../../../components'

import { addSeriesInfo, handleProductLinkClick } from '../../utils/utils'
import { RenderAorB, RenderIf, isEmpty } from '../../../../../../utils/fns'
import {
  escapeCSVValue,
  getICILabel,
  getLatestJobId,
  getModelImage,
  isInvalidOffsetDirection,
  prepareJobData,
} from '../../../../common/fns'
import { warningList } from '../../../../common/json/helpText.json'

import { useModal } from '../../../../../../utils/hooks'
import { reduxBatch } from '@manaflair/redux-batch'
import { HANGER_TYPE } from '../../../../common/constants'
import { COUNTRY, LOADING } from '../../../../../../utils/constants'
import {
  displayFastenersAnchor,
  getFastenersDisplayLabel,
  getFastenersLabel,
  handleOrders,
  updateProductSpec,
} from '../../utils/fastener'

const Result = () => {
  const dispatch = useDispatch()
  const modelRef = useRef([])
  const [show, toggleModal] = useModal(false)

  const { ecMode, loading: uiLoading } = useSelector(getUIState)

  const jobs = useSelector(selectAll)
  const editedJob = useSelector(getEditedJob)
  const isJobExist = useSelector(getExistedJob)
  const jobInputs = useSelector(getJobInputs)

  const hangerType = useSelector((state) => getJobSettingsState(state).hangerType)
  const joistMemberType = useSelector((state) => getJoistState(state).type)
  const { topFlangeOpenClosedAngle, topFlangeSlopedDownAngle, hlcFlush, topFlangeOffset } =
    useSelector(getHangerState)
  const errors = useSelector(getFormErrors)
  const country = useSelector((state) => getSettingsState(state).country)

  const { loading, data, error, sortBy, searchKeyword, optimized, selectedRow, renderedData } =
    useSelector(getResultState)

  const handleAnimation = async (index) => {
    const jobList = document.querySelector('#jobTally__tab')
    const model = modelRef.current[index]

    const clonedModel = model.cloneNode(true)

    const { top: toTop, left: toLeft } = jobList.getBoundingClientRect()
    const { top: fromTop, left: fromLeft } = model.getBoundingClientRect()

    clonedModel.style.position = 'absolute'
    clonedModel.style.zIndex = 999
    clonedModel.style.top = `${fromTop}px`
    clonedModel.style.left = `${fromLeft}px`

    const modelKeyframes = [
      {
        top: `${toTop}px`,
        left: `${toLeft}px`,
        opacity: 0.1,
      },
    ]

    const keyframesOptions = {
      duration: 1000,
    }

    document.body.appendChild(clonedModel)
    await clonedModel.animate(modelKeyframes, keyframesOptions).finished
    clonedModel.remove()
  }

  const handleAddToJobList = async () => {
    if (isJobExist) {
      toggleModal()
    } else {
      const jobData = prepareJobData(jobInputs, selectedRow)
      const newJobID = getLatestJobId(jobs.length + 1)
      handleAnimation(selectedRow.index)
      jobData.modelImg = await getModelImage()
      jobData.outputData = renderedData
      reduxBatch([
        dispatch(jobsActions.jobAdded(jobData)),
        dispatch(jobSettingsActions.modifyInput({ name: 'jobID', value: newJobID })),
      ])

      handleSelectRow(null)
    }
  }

  const handleOrderJobList = () => {
    if (selectedRow && isJobExist) {
      toggleModal()
    } else {
      const currentJobs = selectedRow ? jobs.concat(prepareJobData(jobInputs, selectedRow)) : jobs
      handleOrders(currentJobs)
      reduxBatch(currentJobs.map((j) => dispatch(resultActions.productOrderAction(j))))
    }

    handleSelectRow(null)
  }

  const setSearchKeyword = useCallback((keyword) => {
    dispatch(resultActions.setSearchKeyword(keyword))
  }, [])

  const handleSelectRow = useCallback((data) => {
    dispatch(resultActions.setSelectedRow(data))
  }, [])

  const setOptimized = useCallback(() => {
    dispatch(resultActions.setOptimized())
  }, [])

  const getTableData = useCallback((rows) => {
    dispatch(resultActions.setRenderedData(rows))
  }, [])

  const inputErrors = useMemo(() => {
    return Object.values(errors).join('<br />')
  }, [errors])

  const isUSA = useMemo(() => country === COUNTRY.USA, [country])
  const isIJoist = useMemo(() => joistMemberType === 'ijoist', [joistMemberType])

  // Format the API's response here
  const formattedData = useMemo(() => {
    return data ? data.map(addSeriesInfo).sort((a, b) => a.ici - b.ici) : []
  }, [data])

  const hangerNotTopFlangeMsg = useMemo(() => {
    if (
      (hangerType === HANGER_TYPE.ALL_TYPES &&
        Number(topFlangeOpenClosedAngle) + Number(topFlangeSlopedDownAngle) !== 0) ||
      isInvalidOffsetDirection(hangerType, topFlangeOffset)
    ) {
      return warningList.hangerNotTopFlange
    } else {
      return null
    }
  }, [hangerType, topFlangeOpenClosedAngle, topFlangeSlopedDownAngle, topFlangeOffset])

  const columns = useMemo(() => {
    const resultColumns = [
      {
        id: 'selection',
        Header: () => null,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row, toggleAllRowsSelected, toggleRowSelected }) => {
          // eslint-disable-next-line react/prop-types
          const { series } = row.original
          // eslint-disable-next-line react/prop-types, no-unused-vars
          const { selection, ...values } = row.values
          return (
            <IndeterminateSelectBox
              type="radio"
              // eslint-disable-next-line react/prop-types
              {...row.getToggleRowSelectedProps({
                onChange: () => {
                  // eslint-disable-next-line react/prop-types
                  toggleAllRowsSelected(false) // deselect all.
                  // eslint-disable-next-line react/prop-types
                  toggleRowSelected(row.id, true) // reverse selected status of current row.
                  handleSelectRow({
                    // eslint-disable-next-line react/prop-types
                    id: row.id,
                    // eslint-disable-next-line react/prop-types
                    index: row.index,
                    hangerType,
                    hlcFlush,
                    series,
                    ...values,
                  })
                },
              })}
            />
          )
        },
      },
      {
        Header: 'Model',
        accessor: 'model',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value, row }) => {
          // eslint-disable-next-line react/prop-types
          const { series, seriesIllustration } = row.original
          // eslint-disable-next-line react/prop-types
          const { updatedWebStiffeners, updatedSpec } = row.values

          return (
            // eslint-disable-next-line react/prop-types
            <div ref={(el) => (modelRef.current[row.index] = el)} className="hs__firstCell">
              <div className="hs__modelImage">
                <img src={seriesIllustration} alt={value} />
              </div>

              <div className="hs__modelInfo">
                <Button
                  className="hs__modelLink button"
                  variant="link"
                  onClick={() => handleProductLinkClick(value, series, hangerType)}
                >
                  <span className="button__text">
                    {value}
                    {updatedWebStiffeners}
                    <br />
                    {updatedSpec}
                  </span>
                </Button>
              </div>
            </div>
          )
        },
      },
      {
        Header: 'Installed Cost',
        accessor: 'ici',
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <span>{row.values.iciLabel}</span>,
      },
      {
        Header: 'Width',
        accessor: 'wSize',
        sortType: 'basic',
      },
      {
        Header: 'Height',
        accessor: 'hSize',
        sortType: 'basic',
      },
      {
        Header: 'Bearing',
        accessor: 'bSize',
        sortType: 'basic',
      },
      {
        id: 'tfSize',
        Header: 'TF Depth',
        accessor: (data) => (data.tfSize === 0 ? '-' : data.tfSize),
        sortType: 'basic',
      },
      {
        id: 'tfFastenersLabel',
        Header: 'TF Fasteners',
        accessor: (d) => getFastenersDisplayLabel(d.tfNailQty, d.tfNailType),
        Cell: ({ value, row }) => {
          const { tfNailType } = row.original
          return displayFastenersAnchor(tfNailType, value)
        },
      },
      {
        id: 'fFastenersLabel',
        Header: 'Face Fasteners',
        accessor: (d) => getFastenersDisplayLabel(d.hNailQty[0], d.hNailType[0]),
        Cell: ({ value, row }) => {
          const { hNailType } = row.original
          return displayFastenersAnchor(hNailType[0], value)
        },
      },
      {
        id: 'jFastenersLabel',
        Header: 'Joist Fasteners',
        accessor: (d) => getFastenersDisplayLabel(d.jNailQty, d.jNailType, true),
        Cell: ({ value, row }) => {
          const { jNailType } = row.original
          return displayFastenersAnchor(jNailType, value)
        },
      },
      {
        Header: 'Download (lbs)',
        accessor: 'load',
      },
      {
        Header: 'Uplift (lbs)',
        accessor: 'uplift',
      },
      {
        Header: () => null,
        accessor: 'tfNailQty',
      },
      {
        Header: () => null,
        accessor: 'tfNailType',
      },
      {
        Header: () => null,
        accessor: 'hNailQty',
      },
      {
        Header: () => null,
        accessor: 'hNailType',
      },
      {
        Header: () => null,
        accessor: 'jNailQty',
      },
      {
        Header: () => null,
        accessor: 'jNailType',
      },
      {
        id: 'updatedSpec',
        Header: () => null,
        accessor: (d) => {
          const updatedSpec = updateProductSpec(d.spec, hlcFlush, hangerType)

          return updatedSpec
        },
      },
      {
        id: 'updatedWebStiffeners',
        Header: () => null,
        accessor: (d) => {
          const updatedWebStiffeners = d.webStiffeners ? '*' : ''

          return updatedWebStiffeners
        },
      },
      {
        id: 'iciLabel',
        Header: () => null,
        accessor: (d) => getICILabel(d.ici),
      },
    ]

    return resultColumns
  }, [isIJoist, hlcFlush, hangerType])

  const csvData = useMemo(() => {
    return renderedData.length
      ? renderedData.map((item) => ({
          Model:
            item.model +
            item.updatedWebStiffeners +
            (item.updatedSpec ? `\n${item.updatedSpec}` : ''),
          'Installed Cost': item.iciLabel,
          Width: item.wSize,
          Height: item.hSize,
          Bearing: item.bSize,
          'TF Depth': item.tfSize,
          'TF Fasteners': escapeCSVValue(item.tfFastenersLabel),
          'Face Fasteners': escapeCSVValue(item.fFastenersLabel),
          'Joist Fasteners': escapeCSVValue(item.jFastenersLabel),
          'Download (lbs)': item.load,
          'Uplift (lbs)': item.uplift,
        }))
      : []
  }, [renderedData])

  const getRowId = useCallback((row, relativeIndex, parent) => {
    const tfFasteners = getFastenersLabel(row.tfNailQty, row.tfNailType)
    const fFasteners = getFastenersLabel(row.hNailQty[0], row.hNailType[0])
    const jFasteners = getFastenersLabel(row.jNailQty, row.jNailType, true)

    return parent
      ? [parent.id, relativeIndex].join('.')
      : [row.model, tfFasteners, fFasteners, jFasteners, row.load, row.uplift].join('-')
  }, [])

  const selectedRowIds = useMemo(() => {
    return selectedRow ? { [selectedRow.id]: true } : {}
  }, [editedJob, selectedRow])

  return (
    <div className="space-y-4 my-4">
      {RenderAorB(
        loading === LOADING.PENDING || uiLoading === LOADING.PENDING,
        <LoadingIndicator />,
        RenderAorB(
          loading === LOADING.FAILED,
          <NoticeMessage level="error" icon="error" title="Network Error" description={error} />,
          RenderAorB(
            inputErrors,
            <NoticeMessage
              level="error"
              icon="error"
              title="Validation"
              description={inputErrors}
            />,
            RenderAorB(
              loading === LOADING.SUCCEEDED && isEmpty(data),
              <NoticeMessage
                level="warning"
                icon="warning"
                title="No Output Results"
                description="There are no results based upon the input parameters. Try editing your input criteria, or use the reset button to load the initial conditions."
              />,
              <Fragment>
                <NoticeMessage
                  level="warning"
                  icon="warning"
                  title="Warning"
                  description={hangerNotTopFlangeMsg}
                />
                <div className="d-flex justify-between">
                  <div className="d-flex gap-x-2">
                    <AddToJobList
                      disabled={!selectedRow || !!editedJob}
                      onClick={handleAddToJobList}
                    />
                    {RenderIf(
                      ecMode,
                      <OrderJobList
                        disabled={(!selectedRow && jobs.length === 0) || editedJob !== null}
                        isEditMode={!!editedJob}
                        isMultipleJobs={jobs.length}
                        onClick={handleOrderJobList}
                      />,
                    )}
                  </div>
                  <div>
                    {renderedData.length > 0 ? (
                      <CSVLink
                        className="button -tertiary -xsmall"
                        data={csvData}
                        filename={`hs-output-${dayjs(new Date()).format('YYYYMMDD')}.csv`}
                        style={{
                          cursor: renderedData.length ? 'pointer' : 'not-allowed',
                        }}
                      >
                        DOWNLOAD CSV
                      </CSVLink>
                    ) : (
                      <Button className="button -xsmall" disabled style={{ cursor: 'not-allowed' }}>
                        DOWNLOAD CSV
                      </Button>
                    )}
                  </div>
                </div>

                <ResultTable
                  data={formattedData}
                  columns={columns}
                  optimized={optimized}
                  setOptimized={setOptimized}
                  searchKeyword={searchKeyword}
                  setSearchKeyword={setSearchKeyword}
                  selectedRowIds={selectedRowIds}
                  sortBy={sortBy}
                  getRowId={getRowId}
                  getTableData={getTableData}
                  setSelectedRow={handleSelectRow}
                />

                <TableNotes isIJoistType={isIJoist} isUSA={isUSA} />

                <DuplicateJobError show={show} toggle={toggleModal} />
              </Fragment>,
            ),
          ),
        ),
      )}
    </div>
  )
}

export default Result
